import { CommonModule, DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import { finalize } from 'rxjs/internal/operators/finalize';
import { PageSize } from 'src/app/enumeration/pageSize';
import { LmsApiService } from 'src/app/service/lms/lms-api-service';
import { DateFormatPipe } from "../../../utils/date-format.pipe";
import { DownloadService } from 'src/app/utils/download/download.service';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-lms-course-redeem',
  imports: [
    CommonModule,
    NgxPaginationModule,
    RouterModule,
    DateFormatPipe,
    FormsModule,
    ReactiveFormsModule
  ],
  templateUrl: './lms-course-redeem.html',
  styleUrl: './lms-course-redeem.css',
})
export class LmsCourseRedeem implements OnInit {

  private readonly router = inject(Router);
  private readonly apiService = inject(LmsApiService);
  private readonly fb = inject(FormBuilder);
  private readonly datePipe = inject(DatePipe);
  private readonly downloadService = inject(DownloadService);
  private readonly destroyRef = inject(DestroyRef);

  readonly courseRedeemList = signal<any[]>([]);
  readonly total = signal<number>(0);
  readonly allTotal = signal<number>(0);
  readonly page = signal<number>(1);
  readonly pageSize = signal<number>(PageSize.DEFAULT_LIST);

  readonly apiLoading = signal<boolean>(false);
  readonly loading = signal<boolean>(false);
  readonly isRefundBtnLoading = signal<boolean[]>([]);

  readonly errorMessage = signal<string | null>(null);
  readonly successMessage = signal<string | null>(null);

  downloadFileName = signal<string | null>(null);
  downloading = signal<boolean>(false);

  lmsRedeemStatus = signal<string>('All');
  lmsRedeemStatusOption: any[] = ['All', 'LMS_REDEEM', 'LMS_REFUND'];
  startDate = signal<string | null>(null);
  endDate = signal<string | null>(null);

  phoneParam = input<string | undefined>(undefined, { alias: 'phone' });


  ngOnInit(): void {
    if (this.phoneParam()) {
      this.fetchCourseRedeemRecordsByPhone();
    } else {
      this.fetchCourseRedeemRecords();
    }

  }

  fetchCourseRedeemRecordsByPhone() {
    this.loading.set(true);
    const zeroBasedPage = this.page() - 1;

    this.apiService.getLMSCourseRedeemListByCustomer(this.phoneParam()!, this.lmsRedeemStatus(), null, null, zeroBasedPage)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: (v: any) => {
          this.courseRedeemList.set(v?.Message.lmsCourseRedeemInfoRespLst || []);
          this.total.set(v?.Message.pageTotal || 0);
          this.allTotal.set(v?.Message.allTotal || 0);
        },
        error: (err: any) => {
          this.showError(err || 'Network error occurred while fetching course redeem records by phone.');
        }
      });
  }

  fetchCourseRedeemRecords() {

    let start: string | null = null;
    let end: string | null = null;

    if (!this.startDate() && this.endDate()) {
      alert('Please select start date');
      return;
    }
    if (this.startDate() && !this.endDate()) {
      alert('Please select end date');
      return;
    }

    if (this.startDate() && this.endDate()) {
      const startObj = new Date(this.startDate()!);
      const endObj = new Date(this.endDate()!);

      if (startObj > endObj) {
        alert('Start date cannot be later than the End date');
        return;
      }

      start = this.datePipe.transform(this.startDate(), 'dd/MM/yyyy');
      end = this.datePipe.transform(this.endDate(), 'dd/MM/yyyy');
    }
    this.loading.set(true)

    const zeroBasedPage = this.page() - 1;

    this.apiService.getLMSCourseRedeemList(this.lmsRedeemStatus(), start, end, zeroBasedPage)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.loading.set(false)))
      .subscribe({
        next: (v: any) => {
          this.courseRedeemList.set(v?.Message.lmsCourseRedeemInfoRespLst || []);
          this.total.set(v?.Message.pageTotal || 0);
          this.allTotal.set(v?.Message.allTotal || 0);
        },
        error: (err: any) => {
          this.showError(err || 'Network error occurred while fetching course redeem records.');
        }
      });

  }

  resetForm() {
    this.lmsRedeemStatus.set('All');
    this.startDate.set(null);
    this.endDate.set(null);
    this.page.set(1);
    this.fetchCourseRedeemRecords();
  }

  refundRedeem(data: any, index: number) {
    if (!confirm('Are you sure you want to refund this redeem?')) {
      return;
    }

    this.isRefundBtnLoading.update((prev) => {
      const newState = [...prev];
      newState[index] = true;
      return newState;
    });

    this.apiService.getRefundLMSCourseRedeem(data.id)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => {
        this.isRefundBtnLoading.update((prev) => {
          const newState = [...prev];
          newState[index] = false;
          return newState;
        });
      }))
      .subscribe({
        next: (v: any) => {
          this.showSuccess('Redeem refunded successfully.');
          if (this.phoneParam()) {
            this.fetchCourseRedeemRecordsByPhone();
          } else {
            this.fetchCourseRedeemRecords();
          }
        },
        error: (err: any) => {
          this.showError(err || 'Network error occurred while refunding the redeem.');
        }
      });
  }

  downloadFile(): void {
    if (!this.downloadFileName()?.trim()) return;

    let start: string | null = null;
    let end: string | null = null;

    if (!this.startDate() && this.endDate()) {
      alert('Please select start date');
      return;
    }
    if (this.startDate() && !this.endDate()) {
      alert('Please select end date');
      return;
    }

    if (this.startDate() && this.endDate()) {
      const startObj = new Date(this.startDate()!);
      const endObj = new Date(this.endDate()!);

      if (startObj > endObj) {
        alert('Start date cannot be later than the End date');
        return;
      }

      start = this.datePipe.transform(this.startDate(), 'dd/MM/yyyy');
      end = this.datePipe.transform(this.endDate(), 'dd/MM/yyyy');
    }

    this.downloading.set(true);
    const fileName = this.downloadFileName()?.endsWith('.csv') ? this.downloadFileName() : `${this.downloadFileName()}.csv`;

    this.apiService.getLMSCourseRedeemDownloadFile(fileName, this.lmsRedeemStatus(), start, end).pipe(
      finalize(() => this.downloading.set(false))
    ).subscribe({
      next: (file) => {
        this.downloadService.downloadFileInLocal(file, fileName!);
        this.downloadFileName.set(null);
        this.closeModalViaNativeElement('downloadCSVModal');
      },
      error: (error) => {
        this.downloadFileName.set(null);
        const errorMsg = error?.Object?.statusText || error?.statusText || error?.message || error || 'Failed to download file.';
        this.showError(errorMsg);
      }
    });
  }

  handlePageChange(event: number): void {
    if (!this.loading()) {
      this.page.set(event);
      if (this.phoneParam()) {
        this.fetchCourseRedeemRecordsByPhone();
      } else {
        this.fetchCourseRedeemRecords();
      }
    }
  }

  shouldRenderAdminRefundButton(data: any): boolean {
    const isLMSRedeem = this.lmsRedeemStatus() === 'LMS_REDEEM' && data?.redeemableStatus === 'LMS_REDEEM';

    return isLMSRedeem;

  }

  showError(msg: string): void {
    this.errorMessage.set(msg);
    // Auto clear error banner after 6 seconds
    setTimeout(() => this.clearAlerts(), 6000);
  }

  showSuccess(msg: string): void {
    this.successMessage.set(msg);
    // Auto clear success banner after 4 seconds
    setTimeout(() => this.clearAlerts(), 4000);
  }

  clearAlerts(): void {
    this.errorMessage.set(null);
    this.successMessage.set(null);
  }

  private closeModalViaNativeElement(elementId: string): void {
    const targetModalNode = document.getElementById(elementId);
    if (targetModalNode) {
      const bootstrapInstance = (window as any).bootstrap?.Modal?.getInstance(targetModalNode);
      bootstrapInstance?.hide();
    }
  }

  goBack(): void {
    window.history.back();
  }

}
